<template>
  <div class="p-6 mx-auto">
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">Songs Documentation</h1>
      <p class="text-gray-600 dark:text-gray-400">
        Learn how to manage your song library, add new songs, and organize your collection.
      </p>
    </div>

    <div class="space-y-6">
      <!-- Overview Section -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Overview</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          The Songs page is your central hub for managing the song library. Here you can add, edit,
          disable, and organize all songs available for voting.
        </p>
      </section>

      <!-- Adding Songs -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Adding New Songs</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">To add a new song to your library:</p>
        <ol class="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Click the "Create New Song" button at the top of the Songs page</li>
          <li>
            Fill in the song information:
            <ul class="list-disc list-inside ml-6 mt-2 space-y-1">
              <li><strong>Title:</strong> The song title (required)</li>
              <li><strong>Artist:</strong> The original performer of the song (required)</li>
              <li>
                <strong>Song Length:</strong> Duration of the song in minutes and seconds (e.g.
                3:45). Used by the setlist feature to calculate how many songs fit into a set.
              </li>
              <li>
                <strong>Category:</strong> Categories for the song / multiple categories are allowed
              </li>
              <li><strong>Language:</strong> Language of the song</li>
              <li>
                <strong>Genre:</strong> Select from available genres (optional); Note: currently
                disabled on the voting page
              </li>
              <li>
                <strong>Notes:</strong> Any additional information about the song for your own
                reference (not displayed on the voting page)
              </li>
              <li>
                <strong>Song Status:</strong> Disable the song to temporarily remove it from voting
                without deleting; for example in case the band is not ready to perform the song.
                Note: disabling song will disable it for all future events.
              </li>
            </ul>
          </li>
          <li>Click "Save Song" to add it to your library</li>
        </ol>
      </section>

      <!-- Editing Songs -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Editing Songs</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">To edit an existing song:</p>
        <ol class="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Click the edit icon (pencil) next to the song in the table</li>
          <li>Modify any fields as needed</li>
          <li>Click "Save Song" to apply changes</li>
        </ol>
        <p class="text-gray-600 dark:text-gray-400">
          Note: Changes to songs will affect all future events, but won't change votes already cast.
        </p>
      </section>

      <!-- Disabling Songs -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Song Status</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          You can disable songs to temporarily remove them from voting without deleting them:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Use the toggle switch in the "Status" column to enable/disable a song</li>
          <li>Disabled songs won't appear in voting options for new events</li>
          <li>Disabled songs are still preserved in your library for future use</li>
          <li>You can re-enable songs at any time</li>
        </ul>
      </section>

      <!-- Searching and Filtering -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          Searching and Filtering
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          The Songs page provides several ways to find songs:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>
            <strong>Search Bar:</strong> Type to search by title, author, or original artist etc.
          </li>
          <li><strong>Column Sorting:</strong> Click column headers to sort by that field</li>
          <li>
            <strong>Pagination:</strong> Navigate through pages of songs using the pagination
            controls
          </li>
        </ul>
      </section>

      <!-- Categories and Genres -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          Categories and Genres
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          Songs can be organized using categories and genres:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>
            <strong>Categories:</strong> Broad classifications (e.g., "Pop", "Rock", "Classical")
          </li>
          <li>
            <strong>Genres:</strong> More specific classifications that can be managed separately
          </li>
          <li>Both help organize your library and can be used for filtering in events</li>
          <li>
            Note: "Genre" option is visible on Admin page, but currently disabled on the voting page
            as it was considered to not provide enough value for the voters.
          </li>
        </ul>
        <p class="text-gray-600 dark:text-gray-400">
          Manage categories and genres from the Songs submenu in the sidebar.
        </p>
      </section>

      <!-- Deleting Songs -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Deleting Songs</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">To permanently delete a song:</p>
        <ol class="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Click the delete icon (trash) next to the song</li>
          <li>Confirm the deletion in the dialog</li>
        </ol>
        <p class="text-gray-600 dark:text-gray-400">
          <strong>Warning:</strong> Deleting a song is permanent and cannot be undone. Consider
          disabling the song instead if you might need it later.
        </p>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: "SongsDocumentation"
};
</script>
